import { DesktopLayout } from "../../components/DesktopLayout";
import { useApp } from "../../context/AppContext";
import { formatDataHora } from "../../data/mock";
import { Repeat, ArrowRight, Clock, MessageSquare, Footprints, AlertTriangle } from "lucide-react";

export default function PassagemTurnoDesktop() {
  const { passagens, vigilantes, postos, getById } = useApp();

  const card = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
  };

  return (
    <DesktopLayout
      title="Passagens de Turno"
      subtitle="Resumos enviados pelos vigilantes ao final de cada plantão"
    >
      {/* Resumo */}
      <div
        className="rounded-2xl p-5 mb-6 flex items-center justify-between gap-4"
        style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)" }}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.2)" }}
          >
            <Repeat className="w-5 h-5" style={{ color: "#3B82F6" }} />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.12em] uppercase mb-0.5" style={{ color: "rgba(147,197,253,0.5)" }}>
              Encerramento de turno
            </p>
            <p className="text-[16px] font-bold text-white">
              {passagens.length} passagen{passagens.length !== 1 ? "s" : ""} registrada{passagens.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>

      {/* Timeline chat-style */}
      {passagens.length === 0 ? (
        <div className="rounded-2xl p-12 text-center" style={card}>
          <MessageSquare className="w-10 h-10 mx-auto mb-3" style={{ color: "rgba(255,255,255,0.12)" }} />
          <p className="text-[14px] font-semibold" style={{ color: "rgba(255,255,255,0.35)" }}>Nenhuma passagem registrada.</p>
          <p className="text-[12px] mt-1" style={{ color: "rgba(255,255,255,0.2)" }}>Os vigilantes enviam resumos ao encerrar cada plantão.</p>
        </div>
      ) : (
        <div className="max-w-2xl space-y-4">
          {passagens.map((p) => {
            const v     = getById(vigilantes, p.deVigilanteId);
            const posto = getById(postos, p.postoId);
            const iniciais = (v?.nome || "VG").split(" ").map((n) => n[0]).slice(0, 2).join("");

            return (
              <article
                key={p.id}
                data-testid={`pt-row-${p.id}`}
                className="rounded-2xl overflow-hidden"
                style={card}
              >
                {/* Header da passagem */}
                <div
                  className="px-5 py-4 flex items-center gap-4"
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.05)", background: "rgba(255,255,255,0.02)" }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center text-[12px] font-bold"
                    style={{ background: "rgba(59,130,246,0.12)", color: "#3B82F6" }}
                  >
                    {iniciais}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[14px] font-bold text-white">{v?.nome || "Vigilante"}</span>
                      <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "rgba(255,255,255,0.3)" }} />
                      <span className="text-[13px] font-semibold" style={{ color: "#3B82F6" }}>
                        {p.paraVigilante || "Próximo vigilante"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {posto && (
                        <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.3)" }}>
                          {posto.nome}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-[11px] font-mono" style={{ color: "rgba(255,255,255,0.25)" }}>
                        <Clock className="w-3 h-3" />
                        {formatDataHora(p.criadoEm)}
                      </span>
                    </div>
                  </div>

                  {/* Métricas inline */}
                  <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.15)" }}>
                      <Footprints className="w-3 h-3" style={{ color: "#3B82F6" }} />
                      <span className="text-[12px] font-bold" style={{ color: "#60a5fa" }}>{p.rondasNoTurno}</span>
                      <span className="text-[10px]" style={{ color: "rgba(96,165,250,0.6)" }}>rondas</span>
                    </div>
                    {p.ocorrenciasNoTurno > 0 && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.15)" }}>
                        <AlertTriangle className="w-3 h-3" style={{ color: "#F59E0B" }} />
                        <span className="text-[12px] font-bold" style={{ color: "#fbbf24" }}>{p.ocorrenciasNoTurno}</span>
                        <span className="text-[10px]" style={{ color: "rgba(251,191,36,0.6)" }}>ocorr.</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Corpo: resumo */}
                <div className="px-5 py-4">
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: "rgba(255,255,255,0.25)" }}>
                    Resumo do turno
                  </p>
                  <p className="text-[14px] leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
                    {p.resumo || "Sem resumo informado."}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </DesktopLayout>
  );
}
